import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Icon,
  Money,
  PageHeader,
  Select,
  TextField,
  Toolbar,
  type DataTableColumn,
  type SelectOption,
} from "@/components";
import { formatInteger, pluralize } from "@/lib/format";
import { useDebouncedValue, useSearchParamsInPlace } from "@/lib/hooks";
import { usePublishedFormulas, type FormulaAccess, type PublishedFormulaRow } from "./api";
import { PerfumeName } from "./PerfumeName";
import styles from "./PublishedFormulasPage.module.css";

const PAGE_SIZE = 50;
const ALL = "all";
const ACCESS_OPTIONS: ReadonlyArray<SelectOption> = [
  { value: ALL, label: "Free and paid" },
  { value: "free", label: "Free, with lines" },
  { value: "paid", label: "Paid, without lines" },
];

function isAccess(value: string | null): value is FormulaAccess {
  return value === "free" || value === "paid";
}

/** Formulas published outside the workspace: search them, see what each smells like and what you own of it. */
export function PublishedFormulasPage() {
  const [searchParams, setSearchParams] = useSearchParamsInPlace();
  const q = searchParams.get("q") ?? "";
  const [searchText, setSearchText] = useState(q);
  const [syncedQ, setSyncedQ] = useState(q);
  if (q !== syncedQ) {
    // The URL changed from outside the field (Back, a link, "Clear"): show it.
    setSyncedQ(q);
    setSearchText(q);
  }
  const debouncedSearch = useDebouncedValue(searchText, 250);
  const source = searchParams.get("source");
  const accessParam = searchParams.get("access");
  const material = searchParams.get("material") ?? undefined;
  const usingName = searchParams.get("using") ?? "";
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const formulas = usePublishedFormulas({
    q: q || undefined,
    source: source || undefined,
    access: isAccess(accessParam) ? accessParam : undefined,
    material,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  function update(changes: Record<string, string | null>, { resetPage = true } = {}) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (!value || value === ALL) next.delete(key);
          else next.set(key, value);
        }
        if (resetPage && !("page" in changes)) next.delete("page");
        return next;
      },
      { preventScrollReset: !("page" in changes) },
    );
  }

  useEffect(() => {
    const wanted = debouncedSearch.trim() ? debouncedSearch : "";
    if (debouncedSearch !== searchText || wanted === q) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (wanted) next.set("q", wanted);
        else next.delete("q");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, searchText, q, setSearchParams]);

  const sourceOptions = useMemo<SelectOption[]>(
    () => [
      { value: ALL, label: "All sources" },
      ...(formulas.data?.sources ?? []).map((row) => ({
        value: row.id,
        label: row.name,
        detail: pluralize(row.count, "formula", "formulas"),
      })),
    ],
    [formulas.data?.sources],
  );

  const columns: DataTableColumn<PublishedFormulaRow>[] = [
    {
      id: "name",
      header: "Formula",
      width: "wide",
      cell: (row) => (
        <span className={styles.nameCell}>
          <span className={styles.nameLine}>
            <Link to={`/published-formulas/${row.id}`} className={styles.nameLink}>
              {row.name}
            </Link>
            {row.kind === "gcms_analysis" ? <Badge>{row.partial ? "GC-MS, partial" : "GC-MS"}</Badge> : null}
            {row.access === "paid" ? <Badge>Paid</Badge> : null}
          </span>
          {row.style ? <span className={styles.secondary}>{row.style}</span> : null}
        </span>
      ),
    },
    {
      id: "perfume",
      header: "Smells like",
      cell: (row) =>
        row.perfume || row.smells_like ? (
          <PerfumeName perfume={row.perfume} smellsLike={row.smells_like} size="sm" />
        ) : (
          <span className={styles.missing}>An original</span>
        ),
    },
    {
      id: "description",
      header: "Description",
      width: "wide",
      cell: (row) => <span className={styles.description}>{row.description}</span>,
    },
    {
      id: "materials",
      header: "Materials",
      width: "fit",
      cell: (row) =>
        row.access === "paid" ? (
          row.price_minor !== null ? (
            <Money minor={row.price_minor} currency={row.currency} />
          ) : (
            <span className={styles.missing}>Paid</span>
          )
        ) : (
          <span className={styles.materialsCell}>
            <span>
              {formatInteger(row.matched_count)} of {formatInteger(row.line_count)} matched
            </span>
            {row.stock_counts.in_stock ? (
              <Badge tone="success" icon="check">
                {formatInteger(row.stock_counts.in_stock)} in stock
              </Badge>
            ) : null}
            {row.stock_counts.on_order ? (
              <Badge tone="info" icon="truck">
                {formatInteger(row.stock_counts.on_order)} on order
              </Badge>
            ) : null}
          </span>
        ),
    },
    { id: "source", header: "Source", width: "fit", cell: (row) => row.source.name },
  ];

  const total = formulas.data?.count ?? 0;
  const firstRow = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastRow = Math.min(total, page * PAGE_SIZE);
  const filtersActive = Boolean(q || source || accessParam || material);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Published formulas"
        description="Formulas suppliers publish, with the perfume each smells like. They’re shared and read-only; copy one into your formulas to work on it."
        meta={formulas.data ? <Badge>{formatInteger(total)}</Badge> : null}
      />

      <Toolbar label="Search and filter published formulas">
        <TextField
          label="Search"
          hideLabel
          type="search"
          className={styles.search}
          prefix={<Icon name="search" />}
          placeholder="Name, perfume, brand, style or material"
          value={searchText}
          onValueChange={setSearchText}
          autoComplete="off"
          spellCheck={false}
          size="sm"
        />
        <Select
          label="Source"
          size="sm"
          options={sourceOptions}
          value={source ?? ALL}
          onValueChange={(value) => update({ source: value })}
        />
        <Select
          label="Access"
          size="sm"
          options={ACCESS_OPTIONS}
          value={isAccess(accessParam) ? accessParam : ALL}
          onValueChange={(value) => update({ access: value })}
        />
        {material ? (
          <Button size="sm" icon="x" onClick={() => update({ material: null, using: null })}>
            {usingName ? `Using ${usingName}` : "Using one material"}
          </Button>
        ) : null}
      </Toolbar>

      <DataTable
        caption="Published formulas"
        columns={columns}
        rows={formulas.data?.items}
        getRowId={(row) => row.id}
        loading={formulas.isPending}
        error={formulas.error}
        onRetry={() => void formulas.refetch()}
        stickyHeader
        skeletonRows={10}
        empty={
          filtersActive ? (
            <EmptyState
              variant="inline"
              headingLevel={3}
              icon="search"
              title={q ? `No published formula matches “${q}”` : "No published formulas match these filters"}
              description="Search looks at names, the perfumes they smell like, descriptions, styles and materials."
              actions={
                <Button
                  onClick={() => {
                    setSearchText("");
                    setSearchParams({}, { replace: true });
                  }}
                >
                  Clear search and filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              variant="inline"
              headingLevel={3}
              icon="page"
              title="No published formulas yet"
              description="Fraterworks’ demo formulas arrive with the weekly “published_formulas.fraterworks” job."
            />
          )
        }
      />

      {total > PAGE_SIZE ? (
        <nav className={styles.pager} aria-label="Pages">
          <span className={styles.pagerText}>
            {formatInteger(firstRow)}–{formatInteger(lastRow)} of {formatInteger(total)}
          </span>
          <Button
            size="sm"
            icon="chevronLeft"
            disabled={page <= 1}
            onClick={() => update({ page: page - 1 > 1 ? String(page - 1) : null }, { resetPage: false })}
          >
            Previous
          </Button>
          <Button
            size="sm"
            iconEnd="chevronRight"
            disabled={lastRow >= total}
            onClick={() => update({ page: String(page + 1) }, { resetPage: false })}
          >
            Next
          </Button>
        </nav>
      ) : null}
    </div>
  );
}
